import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { useGetMitreEnterprise } from '@/hooks/content/useGetMitreEnterprise';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import { FormError, FormTypeahead } from '@/components/form/form';

const TypeAHeadOptions = ['Enterprise'];
export const MitreInformationSection = () => {
  const { data: mitreEnterpriseData } = useGetMitreEnterprise();
  const { values, handleChange, handleBlur } =
    useFormikContext<AddDetectionRuleSchemaValues>();

  const tacticsOptions = useMemo(() => {
    if (mitreEnterpriseData && values.taxonomy === 'Enterprise') {
      return mitreEnterpriseData.tactics.map((tactic) => tactic.name);
    }
    return [];
  }, [mitreEnterpriseData, values.taxonomy]);

  const techniquesOptions = useMemo(() => {
    if (values.taxonomy === 'Enterprise' && mitreEnterpriseData) {
      return (
        mitreEnterpriseData.tactics
          .find((tactic) => tactic.name === values.tactic)
          ?.techniques.map((technique) => technique.name) || []
      );
    }
    return [];
  }, [values.taxonomy, values.tactic, mitreEnterpriseData]);

  const techniquesIdsOptions = useMemo(() => {
    if (values.taxonomy === 'Enterprise' && mitreEnterpriseData) {
      return (
        mitreEnterpriseData.tactics
          .find((tactic) => tactic.name === values.tactic)
          ?.techniques.filter(
            (technique) => technique.name === values.technique,
          )
          .map((technique) => technique.identifier) || []
      );
    }
    return [];
  }, [values.taxonomy, values.tactic, values.technique, mitreEnterpriseData]);

  const subTechniquesOptions = useMemo(() => {
    if (values.taxonomy === 'Enterprise' && mitreEnterpriseData) {
      return (
        mitreEnterpriseData.tactics
          .find((tactic) => tactic.name === values.tactic)
          ?.techniques.find((technique) => technique.name === values.technique)
          ?.subtechniques.map((subTechnique) => subTechnique.name) || []
      );
    }
    return [];
  }, [values.taxonomy, values.tactic, values.technique, mitreEnterpriseData]);

  const subTechniquesIdsOptions = useMemo(() => {
    if (values.taxonomy === 'Enterprise' && mitreEnterpriseData) {
      return (
        mitreEnterpriseData.tactics
          .find((tactic) => tactic.name === values.tactic)
          ?.techniques.find((technique) => technique.name === values.technique)
          ?.subtechniques.filter(
            (subTechnique) => subTechnique.name === values.subTechnique,
          )
          .map((subTechnique) => subTechnique.identifier) || []
      );
    }
    return [];
  }, [
    values.taxonomy,
    values.tactic,
    values.technique,
    values.subTechnique,
    mitreEnterpriseData,
  ]);

  return (
    <>
      <Label htmlFor="taxonomy" id="taxonomy-label">
        Taxonomy
      </Label>
      <FormTypeahead
        value={values.taxonomy}
        items={TypeAHeadOptions}
        placeholder="Select taxonomy"
        minChars={0}
        name="taxonomy"
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="taxonomy-label"
        data-testid="taxonomy-typeahead"
      />
      <FormError name="taxonomy" />

      <Spacer size="lg" />

      <Label htmlFor="tactic" id="tactic-label">
        Tactic
      </Label>
      <FormTypeahead
        value={values.tactic}
        items={tacticsOptions}
        placeholder="Select a tactic"
        minChars={0}
        name="tactic"
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="tactic-label"
        data-testid="tactic-typeahead"
      />
      <FormError name="tactic" />

      <Spacer size="lg" />

      <div className="flex flex-grow flex-row gap-2">
        <div className="flex flex-1 flex-col">
          <Label htmlFor="technique" id="technique-label">
            Technique
          </Label>
          <FormTypeahead
            value={values.technique}
            items={techniquesOptions}
            placeholder="Select a technique"
            minChars={0}
            name="technique"
            onChange={handleChange}
            onBlur={handleBlur}
            aria-labelledby="technique-label"
            data-testid="technique-typeahead"
          />
          <FormError name="technique" />
        </div>
        <div className="flex flex-1 flex-col">
          <Label htmlFor="techniqueId" id="techniqueId-label">
            TechniqueID
          </Label>
          <FormTypeahead
            value={values.techniqueId}
            items={techniquesIdsOptions}
            placeholder="Select a technique id"
            minChars={0}
            name="techniqueId"
            onChange={handleChange}
            onBlur={handleBlur}
            aria-labelledby="techniqueId-label"
            data-testid="techniqueId-typeahead"
          />
          <FormError name="techniqueId" />
        </div>
      </div>
      <Spacer size="lg" />
      <div className="flex flex-grow flex-row gap-2">
        <div className="flex flex-1 flex-col">
          <Label htmlFor="subTechnique" id="subTechnique-label">
            Sub-Technique
          </Label>
          <FormTypeahead
            value={values.subTechnique}
            items={subTechniquesOptions}
            placeholder="Select a sub-technique"
            minChars={0}
            onChange={handleChange}
            onBlur={handleBlur}
            name="subTechnique"
            aria-labelledby="subTechnique-label"
            data-testid="subTechnique-typeahead"
          />
          <FormError name="subTechnique" />
        </div>
        <div className="flex flex-1 flex-col">
          <Label htmlFor="subTechniqueId" id="subTechniqueId-label">
            Sub-TechniqueID
          </Label>
          <FormTypeahead
            value={values.subTechniqueId}
            items={subTechniquesIdsOptions}
            placeholder="Select a sub-technique"
            minChars={0}
            onChange={handleChange}
            onBlur={handleBlur}
            name="subTechniqueId"
            aria-labelledby="subTechniqueId-label"
            data-testid="subTechniqueId-typeahead"
          />
          <FormError name="subTechniqueId" />
        </div>
      </div>
    </>
  );
};
